import React from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Controller, useForm, useWatch } from "react-hook-form";
import dayjs from "dayjs";
import { computationTypeOptions, depreciationFrequencyOptions, assetTypeOptions, assetAccount, accumulatedDepreciationAccount, depreciationMethodOptions, assetLifeCounterTypeOptions } from "../../../../../../constants/data";
import styles from "./style";
import { AssetFormValidate } from "../../../../../../helper/Validation";
const AssetCreateaPage = ({ createNewAsset }) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });

  const depreciationMethod = useWatch({ control, name: "depreciationMethod" });
  const onSubmit = (data) => {
    const { depreciationPercentage, ...baseData } = data;

    let payload = {
      ...baseData,
      purchaseDate: baseData.purchaseDate
        ? dayjs(baseData.purchaseDate).format("YYYY-MM-DD")
        : null,
      warrantyExpirationDate: baseData.warrantyExpirationDate
        ? dayjs(baseData.warrantyExpirationDate).format("YYYY-MM-DD")
        : null,
      depreciationStartDate: baseData.depreciationStartDate
        ? dayjs(baseData.depreciationStartDate).format("YYYY-MM-DD")
        : null,
    };

    if (data.depreciationMethod === "Declining Balance") {
      payload.depreciationPercentage = depreciationPercentage;
    }
    createNewAsset(payload);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box px="20px" mt={2}>
        <Box sx={styles.header}>
          <Typography sx={styles.title}>Add Assets</Typography>
        </Box>
      </Box>

      <Box sx={styles.container}>
        <Box sx={styles.formSection}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Asset Name</Typography>
                <Controller
                  name="assetName"
                  control={control}
                  rules={AssetFormValidate.assetName}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <OutlinedInput
                        {...field}
                        fullWidth
                        size="small"
                        sx={{
                          ...styles.textField,
                          ...(error && styles.errorField)
                        }}
                        placeholder="Enter Asset Name"
                        error={!!error}
                      />
                      {error && (
                        <Typography sx={styles.errorText}>
                          {error.message}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Asset #</Typography>
                <Controller
                  name="assetNo"
                  control={control}
                  rules={AssetFormValidate.assetNumber}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <OutlinedInput
                        {...field}
                        fullWidth
                        size="small"
                        sx={{
                          ...styles.textField,
                          ...(error && styles.errorField)
                        }}
                        error={!!error}
                        placeholder="Asset Number"
                      />
                      {error && (
                        <Typography sx={styles.errorText}>
                          {error.message}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Box>
            </Grid>

            {/* Purchase Value */}
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Purchase Value</Typography>
                <Controller
                  name="purchaseValue"
                  control={control}
                  rules={AssetFormValidate.purchaseValue}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <OutlinedInput
                        {...field}
                        fullWidth
                        size="small"
                        sx={{
                          ...styles.textField,
                          ...(error && styles.errorField)
                        }}
                        error={!!error}
                        placeholder="Purchase Value"
                      />
                      {error && (
                        <Typography sx={styles.errorText}>
                          {error.message}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Box>
            </Grid>

            {/* Purchase Date */}
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Purchase Date</Typography>
                <Controller
                  name="purchaseDate"
                  control={control}
                  sx={{ width: "100%" }}
                  rules={AssetFormValidate.purchaseDate}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          {...field}
                          inputFormat="DD/MM/YYYY"
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              size="small"
                              placeholder="DD/MM/YYYY"
                              sx={styles.dateTextField}
                            />
                          )}
                        />
                      </LocalizationProvider>
                      {error && (
                        <Typography sx={styles.errorText}>
                          {error.message}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Box>
            </Grid>

            {/* Serial Number */}
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Serial Number</Typography>
                <Controller
                  name="serialNumber"
                  control={control}
                  rules={AssetFormValidate.serialNumber}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <OutlinedInput
                        {...field}
                        fullWidth
                        size="small"
                        sx={{
                          ...styles.textField,
                          ...(error && styles.errorField)
                        }}
                        error={!!error}
                        placeholder="Serial Number"
                      />
                      {error && (
                        <Typography sx={styles.errorText}>
                          {error.message}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Box>
            </Grid>

            {/* Current Value */}
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Current Value</Typography>
                <Controller
                  name="currentValue"
                  control={control}
                  rules={AssetFormValidate.currentValue}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <OutlinedInput
                        {...field}
                        fullWidth
                        size="small"
                        sx={{
                          ...styles.textField,
                          ...(error && styles.errorField)
                        }}
                        error={!!error}
                        placeholder="Current Value"
                      />
                      {error && (
                        <Typography sx={styles.errorText}>
                          {error.message}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Box>
            </Grid>

            {/* Disposal Value */}
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Disposal Value</Typography>
                <Controller
                  name="disposalValue"
                  control={control}
                  rules={AssetFormValidate.disposalValue}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <OutlinedInput
                        {...field}
                        fullWidth
                        size="small"
                        sx={{
                          ...styles.textField,
                          ...(error && styles.errorField)
                        }}
                        error={!!error}
                        placeholder="Disposal Value"
                      />
                      {error && (
                        <Typography sx={styles.errorText}>
                          {error.message}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Box>
            </Grid>

            {/* Asset Type (Select) */}
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Asset Type</Typography>
                <Controller
                  name="assetType"
                  control={control}
                  rules={AssetFormValidate.assetType}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Select
                        {...field}
                        fullWidth
                        displayEmpty
                        size="small"
                        defaultValue=""
                        sx={{
                          ...styles.textField,
                          ...(error && styles.errorField)
                        }}
                        error={!!error}
                      >
                        <MenuItem value="">
                          <span style={{ color: "#999" }}>Select Asset Type</span>
                        </MenuItem>
                        {assetTypeOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {error && (
                        <Typography sx={styles.errorText}>
                          {error.message}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Box>
            </Grid>

            {/* Warranty Expiration Date */}
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>
                  Warranty Expiration Date
                </Typography>
                <Controller
                  name="warrantyExpirationDate"
                  control={control}
                  rules={AssetFormValidate.warrantyExpirationDate}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          {...field}
                          inputFormat="DD/MM/YYYY"
                          renderInput={(params) => (
                            <OutlinedInput
                              {...params.inputProps}
                              fullWidth
                              size="small"
                              sx={{
                                ...styles.textField,
                                ...(error && styles.errorField)
                              }}
                              error={!!error}
                              placeholder="DD/MM/YYYY"
                            />
                          )}
                        />
                      </LocalizationProvider>
                      {error && (
                        <Typography sx={styles.errorText}>
                          {error.message}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Box>
            </Grid>

            {/* Description */}
            <Grid item xs={12} md={12}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Description</Typography>
                <Controller
                  name="description"
                  control={control}
                  rules={AssetFormValidate.description}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <OutlinedInput
                        {...field}
                        fullWidth
                        multiline
                        minRows={2}
                        sx={styles.multilineTextField}
                        placeholder="Enter Description"
                      />
                      {error && (
                        <Typography sx={styles.errorText}>
                          {error.message}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Box sx={styles.itemSectionDivide}>
        <Box sx={{ backgroundColor: "#28AA4A", width: "2%", height: 2 }}></Box>
        <Typography sx={styles.gridTextDivide}>
          Depreciation Details
        </Typography>
      </Box>
      <Box sx={styles.container}>
        <Box sx={styles.formSection}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Depreciation Method</Typography>
                <Controller
                  name="depreciationMethod"
                  control={control}
                  rules={AssetFormValidate.depreciationMethod}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Select
                        {...field}
                        fullWidth
                        displayEmpty
                        size="small"
                        defaultValue=""
                        sx={{
                          ...styles.textField,
                          ...(error && styles.errorField)
                        }}
                        error={!!error}
                      >
                        <MenuItem value="">
                          <span style={{ color: "#999" }}>Select Depreciation Method</span>
                        </MenuItem>
                        {depreciationMethodOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {error && (
                        <Typography sx={styles.errorText}>
                          {error.message}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Box>
            </Grid>
            {depreciationMethod === "Declining Balance" && (
              <Grid item xs={12} md={4}>
                <Box sx={styles.field}>
                  <Typography sx={styles.label}>Depreciation Percentage</Typography>
                  <Controller
                    name="depreciationPercentage"
                    control={control}
                    rules={AssetFormValidate.depreciationPercentage}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <OutlinedInput
                          {...field}
                          fullWidth
                          size="small"
                          sx={{
                            ...styles.textField,
                            ...(error && styles.errorField)
                          }}
                          error={!!error}
                          placeholder="Enter Depreciation Percentage"
                        />
                        {error && (
                          <Typography sx={styles.errorText}>
                            {error.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Box>
              </Grid>
            )}
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Computation Type</Typography>
                <Controller
                  name="computationType"
                  control={control}
                  rules={AssetFormValidate.computationType}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Select
                        {...field}
                        fullWidth
                        displayEmpty
                        size="small"
                        sx={{
                          ...styles.textField,
                          ...(error && styles.errorField)
                        }}
                        error={!!error}
                        defaultValue=""
                      >
                        <MenuItem value="">
                          <span style={{ color: "#999" }}>Select Computation Type</span>
                        </MenuItem>
                        {computationTypeOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {error && (
                        <Typography sx={styles.errorText}>
                          {error.message}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Depreciation Frequency</Typography>
                <Controller
                  name="depreciationFrequency"
                  control={control}
                  rules={AssetFormValidate.depreciationFrequency}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Select
                        {...field}
                        fullWidth
                        displayEmpty
                        size="small"
                        sx={{
                          ...styles.textField,
                          ...(error && styles.errorField)
                        }}
                        error={!!error}
                        defaultValue=""
                      >
                        <MenuItem value="">
                          <span style={{ color: "#999" }}>Select Depreciation Frequency</span>
                        </MenuItem>
                        {depreciationFrequencyOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {error && (
                        <Typography sx={styles.errorText}>
                          {error.message}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>
                  Depreciation Start Date
                </Typography>
                <Controller
                  name="depreciationStartDate"
                  control={control}
                  rules={AssetFormValidate.depreciationStartDate}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          {...field}
                          inputFormat="DD/MM/YYYY"
                          renderInput={(params) => (
                            <OutlinedInput
                              {...params.inputProps}
                              fullWidth
                              size="small"
                              sx={{
                                ...styles.textField,
                                ...(error && styles.errorField)
                              }}
                              error={!!error}
                              placeholder="DD/MM/YYYY"
                            />
                          )}
                        />
                      </LocalizationProvider>
                      {error && (
                        <Typography sx={styles.errorText}>
                          {error.message}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Asset Life</Typography>
                <Controller
                  name="assetLifeCounter"
                  control={control}
                  rules={AssetFormValidate.assetLife}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <OutlinedInput
                        {...field}
                        fullWidth
                        size="small"
                        sx={{
                          ...styles.textField,
                          ...(error && styles.errorField)
                        }}
                        error={!!error}
                        placeholder="Enter Asset Life"
                      />
                      {error && (
                        <Typography sx={styles.errorText}>
                          {error.message}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Asset Life Type</Typography>
                <Controller
                  name="assetLifeCounterType"
                  control={control}
                  rules={AssetFormValidate.assetLifeType}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Select
                        {...field}
                        fullWidth
                        displayEmpty
                        size="small"
                        sx={{
                          ...styles.textField,
                          ...(error && styles.errorField)
                        }}
                        error={!!error}
                        defaultValue=""
                      >
                        <MenuItem value="">
                          <span style={{ color: "#999" }}>Select Depreciation Frequency</span>
                        </MenuItem>
                        {assetLifeCounterTypeOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {error && (
                        <Typography sx={styles.errorText}>
                          {error.message}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Box sx={styles.itemSectionDivide}>
        <Box sx={{ backgroundColor: "#28AA4A", width: "2%", height: 2 }}></Box>
        <Typography sx={styles.gridTextDivide}>
          Account Details
        </Typography>
      </Box>
      <Box sx={styles.container}>
        <Box sx={styles.formSection}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Asset Account</Typography>
                <Controller
                  name="assetAccount"
                  control={control}
                  rules={AssetFormValidate.assetAccount}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Select
                        {...field}
                        fullWidth
                        displayEmpty
                        size="small"
                        defaultValue=""
                        sx={{
                          ...styles.textField,
                          ...(error && styles.errorField)
                        }}
                        error={!!error}
                      >
                        <MenuItem value="">
                          <span style={{ color: "#999" }}>Select Asset Account</span>
                        </MenuItem>
                        {assetAccount.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {error && (
                        <Typography sx={styles.errorText}>
                          {error.message}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Accumulated Depreciation Account</Typography>
                <Controller
                  name="accumulatedDepreciationAccount"
                  control={control}
                  rules={AssetFormValidate.accumulatedDepreciationAccount}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Select
                        {...field}
                        fullWidth
                        displayEmpty
                        size="small"
                        sx={{
                          ...styles.textField,
                          ...(error && styles.errorField)
                        }}
                        error={!!error}
                        defaultValue=""
                      >
                        <MenuItem value="">
                          <span style={{ color: "#999" }}>Select Depreciation Account</span>
                        </MenuItem>
                        {accumulatedDepreciationAccount.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {error && (
                        <Typography sx={styles.errorText}>
                          {error.message}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Depreciation Expense Account</Typography>
                <Controller
                  name="depreciationExpenseAccount"
                  control={control}
                  rules={AssetFormValidate.depreciationExpenseAccount}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Select
                        {...field}
                        fullWidth
                        displayEmpty
                        size="small"
                        sx={{
                          ...styles.textField,
                          ...(error && styles.errorField)
                        }}
                        error={!!error}
                        defaultValue=""
                      >
                        <MenuItem value="">
                          <span style={{ color: "#999" }}>Select Depreciation Account</span>
                        </MenuItem>
                        {depreciationFrequencyOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {error && (
                        <Typography sx={styles.errorText}>
                          {error.message}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
      {/* Buttons */}
      <Box sx={styles.actionButtons}>
        <Button type="submit" variant="contained" sx={styles.saveButton}>
          Save
        </Button>
        <Button variant="outlined" sx={styles.cancelButton}>
          Cancel
        </Button>
      </Box>
    </form>
  );
};

export default AssetCreateaPage;
